package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Image
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.data.AdminRepo
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminStoreSaleMethod
import org.gymly.model.AdminStoreSaleStatus
import org.gymly.model.PickedImagePayload
import org.gymly.theme.AdminColors
import com.gymly.ui.finance.PaymentUiState
import org.gymly.ui.components.FileUploadIconButton
// TODO(IA): escáner de comprobantes suspendido; reactivar con el módulo de IA.
// import org.gymly.ui.components.PaymentScannerButton
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import org.gymly.util.openProofFilePicker
import org.gymly.util.todayIsoDate
import org.gymly.ui.components.AdminDateField
import org.gymly.util.AdminDateOutputFormat

data class PaymentLineDraft(
    val method: AdminPaymentMethod = AdminPaymentMethod.CASH,
    val amountInput: String = "",
    val vesAmountInput: String = "",
    val payerPhone: String = "",
    val notes: String = "",
    val proof: PickedImagePayload? = null,
    val proofName: String? = null,
    /** Mientras es true, al elegir método se sincronizan USD/Bs. Al editar a mano pasa a false. */
    val dualCurrencyLinked: Boolean = true,
) {
    fun amountCents(defaultAmountInput: String): Long =
        parseBulkAmountToCents(amountInput.ifBlank { defaultAmountInput })
}

/** Últimos 5 dígitos para pago móvil; resto de métodos: trim. */
fun normalizePaymentReference(method: AdminPaymentMethod, raw: String): String {
    val trimmed = raw.trim()
    if (trimmed.isEmpty()) return ""
    if (method != AdminPaymentMethod.MOBILE_PAYMENT) return trimmed
    val digits = trimmed.filter { it.isDigit() }
    return if (digits.length <= 5) digits else digits.takeLast(5)
}

fun paymentLineReferenceInput(method: AdminPaymentMethod, raw: String): String =
    if (method == AdminPaymentMethod.MOBILE_PAYMENT) {
        raw.filter { it.isDigit() }.takeLast(5)
    } else {
        raw
    }

fun paymentLinesBsCents(
    lines: List<PaymentLineDraft>,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Long = lines.sumOf { line ->
    if (line.method.usesUsdAmount()) 0L
    else foreignCurrencyLineAmountCents(line, defaultAmount, quote)
}

/** Suma en USD solo de métodos en dólares (divisas / USDT / Zelle). */
fun paymentLinesUsdCents(
    lines: List<PaymentLineDraft>,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Long = lines.sumOf { line ->
    if (!line.method.usesUsdAmount()) 0L
    else foreignCurrencyLineUsdCents(line, defaultAmount, quote) ?: 0L
}

/** Totales pagados por método (cada uno en su moneda; nunca 3000+5=3005). */
fun paymentLinesPaidTotalsLabel(
    lines: List<PaymentLineDraft>,
    defaultAmount: String,
    quote: AdminExchangeRateQuote? = null,
): String {
    if (lines.isEmpty()) return "—"
    val byMethod = paymentLinesByMethodLabels(lines, defaultAmount, quote)
    if (byMethod.isNotEmpty()) return byMethod.joinToString(" · ")
    val bs = paymentLinesBsCents(lines, defaultAmount, quote)
    val usd = paymentLinesUsdCents(lines, defaultAmount, quote)
    return buildList {
        if (bs > 0) add(formatMoney(bs.toInt(), GymlyCurrency.CODE))
        if (usd > 0) add(formatMoney(usd.toInt(), "USD"))
    }.joinToString(" · ").ifBlank { formatMoney(0, GymlyCurrency.CODE) }
}

/** Una etiqueta por método: «Efectivo Bs. 5.000» · «Divisas $20,00». */
fun paymentLinesByMethodLabels(
    lines: List<PaymentLineDraft>,
    defaultAmount: String,
    quote: AdminExchangeRateQuote? = null,
): List<String> {
    if (lines.isEmpty()) return emptyList()
    val order = linkedMapOf<AdminPaymentMethod, Pair<Long, Long>>()
    for (line in lines) {
        val prev = order[line.method] ?: (0L to 0L)
        if (line.method.usesUsdAmount()) {
            val usd = foreignCurrencyLineUsdCents(line, defaultAmount, quote) ?: 0L
            if (usd > 0) order[line.method] = prev.first to (prev.second + usd)
        } else {
            val bs = foreignCurrencyLineAmountCents(line, defaultAmount, quote)
            if (bs > 0) order[line.method] = (prev.first + bs) to prev.second
        }
    }
    return order.mapNotNull { (method, amounts) ->
        val (bs, usd) = amounts
        when {
            method.usesUsdAmount() && usd > 0 -> "${method.labelEs()} ${formatMoney(usd.toInt(), "USD")}"
            !method.usesUsdAmount() && bs > 0 -> "${method.labelEs()} ${formatMoney(bs.toInt(), GymlyCurrency.CODE)}"
            else -> null
        }
    }
}

fun AdminPaymentMethod.requiresReferenceConfigured(
    types: List<org.gymly.model.AdminPaymentMethodType>?,
): Boolean {
    val configured = types?.firstOrNull { it.code.equals(name, ignoreCase = true) }
    return configured?.requiresReference ?: requiresReference()
}

fun AdminPaymentMethod.requiresProofConfigured(
    types: List<org.gymly.model.AdminPaymentMethodType>?,
): Boolean {
    val configured = types?.firstOrNull { it.code.equals(name, ignoreCase = true) }
    return configured?.requiresProof ?: requiresProofAttachment()
}

/** Clave comparable de referencia (pago móvil: últimos 5 dígitos). */
fun paymentReferenceLookupKey(method: AdminPaymentMethod, raw: String): String {
    val normalized = normalizePaymentReference(method, raw)
    if (normalized.isBlank()) return ""
    val digits = normalized.filter { it.isDigit() }
    return if (method == AdminPaymentMethod.MOBILE_PAYMENT || digits.length >= 5) {
        digits.takeLast(5).ifBlank { normalized.lowercase() }
    } else {
        normalized.lowercase()
    }
}

/** Métodos que llevan referencia del pago: el aviso de referencia repetida aplica a todos. */
private val PAYMENT_REFERENCE_METHODS = setOf(
    AdminPaymentMethod.MOBILE_PAYMENT,
    AdminPaymentMethod.TRANSFER,
    AdminPaymentMethod.ZELLE,
    AdminPaymentMethod.USDT,
)

/**
 * Aviso de referencia repetida (no bloquea el cobro). [registeredRefs] = clave → dónde está ("Orden #503"),
 * de la misma fecha de pago.
 */
fun paymentReferenceDuplicateWarning(
    line: PaymentLineDraft,
    siblingLines: List<PaymentLineDraft>,
    knownMobileRefs: Set<String>,
    registeredRefs: Map<String, String> = emptyMap(),
): String? {
    if (line.method !in PAYMENT_REFERENCE_METHODS) return null
    val refKey = paymentReferenceLookupKey(line.method, line.notes)
    if (refKey.length < 5) return null

    val dupInForm = siblingLines.count { other ->
        other.method in PAYMENT_REFERENCE_METHODS &&
            paymentReferenceLookupKey(other.method, other.notes) == refKey
    } > 1
    if (dupInForm) {
        return "Alerta: la referencia $refKey ya está en otra línea de este cobro."
    }
    registeredRefs[refKey]?.let { where ->
        return "Alerta: la referencia $refKey ya fue registrada en $where con la misma fecha de pago."
    }
    if (refKey in knownMobileRefs) {
        return "Alerta: la referencia $refKey ya está en otro pago de esta planilla."
    }
    return null
}

/** Referencias de la fecha de pago (pagos + ventas de tienda): clave → dónde está ("Orden #503", "Venta #163"). */
suspend fun loadRegisteredPaymentRefs(
    dateIso: String,
    excludePaymentId: String? = null,
    excludeStoreSaleId: String? = null,
): Map<String, String> =
    runCatching { AdminRepo.api.paymentReferencesOnDay(dateIso.take(10)) }.getOrElse { emptyList() }
        .filterNot { ref ->
            (excludePaymentId != null && ref.paymentId == excludePaymentId) ||
                (excludeStoreSaleId != null && ref.storeSaleId == excludeStoreSaleId)
        }
        .associate { it.key to it.label }

/**
 * Bs y $ en el mismo cobro. Sin conversión por tasa no se comparan totales: el cobro
 * solo se valida si es todo en Bs o todo en $.
 */
fun isMixedCurrencyPayment(sumBsCents: Long, paidUsdCents: Long): Boolean =
    sumBsCents > 0L && paidUsdCents > 0L

/**
 * @param enforcePlanCoverage si false (alta masiva / órdenes), no bloquea confirmar aunque la suma
 * no cubra el plan; solo se muestran totales Bs y $.
 * Solo Bs se compara con el total en Bs; solo $ se compara con el plan en $; mezcla Bs y $ no se valida.
 */
fun paymentSumValidationError(
    sumCents: Long,
    targetCents: Long,
    allowOverpay: Boolean = false,
    enforcePlanCoverage: Boolean = true,
    paidUsdCents: Long = 0L,
    planUsdCents: Long = 0L,
): String? {
    if (!enforcePlanCoverage) return null
    if (targetCents <= 0 && planUsdCents <= 0) {
        return "Indica un monto total válido."
    }
    if (isMixedCurrencyPayment(sumCents, paidUsdCents)) return null
    val covered = if (paidUsdCents > 0) {
        planUsdCents > 0 && paidUsdCents >= planUsdCents
    } else {
        targetCents > 0 && sumCents >= targetCents
    }
    if (covered) {
        if (!allowOverpay && sumCents > targetCents && targetCents > 0 && paidUsdCents <= 0) {
            return "La suma (${formatMoney(sumCents.toInt(), GymlyCurrency.CODE)}) debe igualar el total " +
                "(${formatMoney(targetCents.toInt(), GymlyCurrency.CODE)})."
        }
        return null
    }
    return buildString {
        append("El cobro no cubre el total")
        if (targetCents > 0) {
            append(" (${formatMoney(sumCents.toInt(), GymlyCurrency.CODE)} / ${formatMoney(targetCents.toInt(), GymlyCurrency.CODE)})")
        }
        if (planUsdCents > 0) {
            append(" · $ (${formatMoney(paidUsdCents.toInt(), "USD")} / ${formatMoney(planUsdCents.toInt(), "USD")})")
        }
        append(".")
    }
}

fun walletOverpayHint(sumCents: Long, targetCents: Long, multiClient: Boolean = false): String? {
    if (sumCents <= targetCents) return null
    val excess = sumCents - targetCents
    return if (multiClient) {
        "Excedente de ${formatMoney(excess.toInt(), GymlyCurrency.CODE)} se repartirá en el monedero digital de cada socio."
    } else {
        "Excedente de ${formatMoney(excess.toInt(), GymlyCurrency.CODE)} se acreditará al monedero digital del cliente."
    }
}

fun paymentReferenceValidationError(
    line: PaymentLineDraft,
    types: List<org.gymly.model.AdminPaymentMethodType>? = null,
): String? {
    if (!line.method.requiresReferenceConfigured(types)) return null
    val ref = normalizePaymentReference(line.method, line.notes)
    if (ref.isBlank()) {
        return if (line.method == AdminPaymentMethod.MOBILE_PAYMENT) {
            "Indica los últimos 5 dígitos de la referencia del pago móvil."
        } else {
            "Indica la referencia del pago."
        }
    }
    if (line.method == AdminPaymentMethod.MOBILE_PAYMENT && ref.length < 5) {
        return "La referencia de pago móvil debe tener 5 dígitos (los últimos del comprobante)."
    }
    return null
}

/**
 * Une líneas de pago móvil (u otras con misma ref) sumando montos — un pago para varias personas.
 * Misma ref = alerta en UI (no bloquea); aquí se consolida el monto.
 */
fun mergeSharedPaymentLines(lines: List<PaymentLineDraft>): List<PaymentLineDraft> {
    if (lines.size <= 1) return lines
    val order = linkedMapOf<String, PaymentLineDraft>()
    var uniqueSeq = 0
    for (line in lines) {
        val ref = normalizePaymentReference(line.method, line.notes)
        // Solo fusionar cuando hay referencia compartida (p. ej. un pago móvil para varios).
        val key = if (line.method.requiresReference() && ref.isNotBlank()) {
            "${line.method.name}|$ref"
        } else {
            "uniq-${uniqueSeq++}"
        }
        val existing = order[key]
        if (existing == null) {
            order[key] = line.copy(notes = ref, payerPhone = "")
        } else if (existing.method.usesUsdAmount() && line.method.usesUsdAmount()) {
            val sumUsd = parseBulkAmountToCents(existing.amountInput) + parseBulkAmountToCents(line.amountInput)
            order[key] = existing.copy(
                amountInput = centsToAmountInput(sumUsd),
                vesAmountInput = "",
                proof = existing.proof ?: line.proof,
                proofName = existing.proofName ?: line.proofName,
                notes = ref,
                payerPhone = "",
            )
        } else if (!existing.method.usesUsdAmount() && !line.method.usesUsdAmount()) {
            val a = parseBulkAmountToCents(existing.amountInput)
            val b = parseBulkAmountToCents(line.amountInput)
            // Si una fila trae 0 (solo ref compartida), conservar el monto positivo de la otra;
            // si ambas tienen monto, sumar (un pago móvil que cubre N personas).
            val sumBs = when {
                a > 0L && b > 0L -> a + b
                a > 0L -> a
                else -> b
            }
            order[key] = existing.copy(
                amountInput = centsToAmountInput(sumBs),
                vesAmountInput = "",
                proof = existing.proof ?: line.proof,
                proofName = existing.proofName ?: line.proofName,
                notes = ref,
                payerPhone = "",
            )
        } else {
            order["uniq-${uniqueSeq++}"] = line.copy(notes = ref, payerPhone = "")
        }
    }
    return order.values.toList()
}

@Composable
fun CombinedPaymentLinesForm(
    totalAmountInput: String,
    defaultLineAmount: String,
    enabled: Boolean = true,
    modifier: Modifier = Modifier,
    linesPerRow: Int = 1,
    allowOverpay: Boolean = false,
    /** Alta masiva / órdenes: no exigir que la suma cubra el plan al confirmar. */
    enforcePlanCoverage: Boolean = true,
    multiClientOverpay: Boolean = false,
    memberId: String? = null,
    clientId: String? = null,
    membershipOrderId: String? = null,
    salesOrderId: String? = null,
    deferWalletApply: Boolean = false,
    showWalletBanner: Boolean = true,
    walletHighlighted: Boolean = false,
    walletInitialAppliedCents: Long = 0L,
    /** Líneas ya configuradas al reabrir Cobrar. */
    initialLines: List<PaymentLineDraft> = emptyList(),
    /** Referencias de pago móvil ya cargadas en otras filas (borrador planilla). */
    extraKnownRefs: Set<String> = emptySet(),
    /**
     * Si true, también alerta contra referencias ya registradas en el sistema con la misma fecha de pago.
     * En alta masiva (borrador) dejar false: solo otras filas / líneas de la planilla.
     */
    checkExistingMobilePayments: Boolean = false,
    /** Fecha de pago del cobro (yyyy-MM-dd): el aviso de referencia repetida compara contra ese día. */
    paymentDateIso: String = todayIsoDate(),
    /** Si true, el desglose muestra el campo de fecha de pago justo debajo del título (ventas de tienda). */
    paymentDateEditable: Boolean = false,
    onPaymentDateChange: (String) -> Unit = {},
    /** Vuelto en Bs ya indicado al reabrir Cobrar. */
    initialChangeGivenCents: Long = 0L,
    /** Vuelto en USD ya indicado al reabrir Cobrar. */
    initialChangeGivenUsdCents: Long = 0L,
    /** Precio USD del plan (catálogo) para precargar divisas/USDT. */
    defaultUsdAmount: String = "",
    /**
     * USD del plan en catálogo solo para mostrar (p. ej. orden con complemento: [defaultUsdAmount] es el pendiente).
     * null = usar [defaultUsdAmount] como precio del plan.
     */
    planCatalogUsdAmount: String? = null,
    /** Etiqueta de la referencia de monto (p. ej. «Plan» o «Por cobrar»). */
    referenceLabel: String = "Plan",
    onCreditApplied: (appliedCents: Long) -> Unit = {},
    onWalletBalanceLoaded: (balanceCents: Long) -> Unit = {},
    onChangeGivenCentsChange: (Long) -> Unit = {},
    onChangeGivenUsdCentsChange: (Long) -> Unit = {},
    onLinesChange: (List<PaymentLineDraft>) -> Unit = {},
    content: @Composable (lines: List<PaymentLineDraft>, formError: String?, changeGivenCents: Long, validate: () -> List<PaymentLineDraft>?) -> Unit,
) {
    val lines = remember(initialLines) {
        mutableStateListOf<PaymentLineDraft>().also { list ->
            list.addAll(
                initialLines.ifEmpty {
                    listOf(PaymentLineDraft(amountInput = defaultLineAmount))
                },
            )
        }
    }
    var formError by remember { mutableStateOf<String?>(null) }
    var changeGivenInput by remember(initialChangeGivenCents) {
        mutableStateOf(
            if (initialChangeGivenCents > 0) centsToAmountInput(initialChangeGivenCents) else "",
        )
    }
    var changeGivenUsdInput by remember(initialChangeGivenUsdCents) {
        mutableStateOf(
            if (initialChangeGivenUsdCents > 0) centsToAmountInput(initialChangeGivenUsdCents) else "",
        )
    }
    var exchangeQuote by remember { mutableStateOf<AdminExchangeRateQuote?>(null) }
    var paymentTypes by remember { mutableStateOf<List<org.gymly.model.AdminPaymentMethodType>?>(null) }
    var knownMobileRefsDb by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var walletModuleEnabled by remember { mutableStateOf(false) }
    var exchangeRatesEnabled by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        val modules = runCatching { AdminRepo.api.getSettingsModules() }.getOrNull()
        exchangeRatesEnabled = modules?.exchangeRatesEnabled == true
        // Con "Usar tasa del BCV en divisas" apagado, la tasa no se usa ni se pide.
        exchangeQuote = if (exchangeRatesEnabled) runCatching { AdminRepo.api.currentExchangeRate() }.getOrNull() else null
        paymentTypes = runCatching { AdminRepo.api.listPaymentMethodTypes().methods }.getOrNull()
        walletModuleEnabled = modules?.clientAccountsEnabled == true
    }

    LaunchedEffect(checkExistingMobilePayments, paymentDateIso) {
        if (checkExistingMobilePayments) {
            knownMobileRefsDb = loadRegisteredPaymentRefs(paymentDateIso)
        }
    }

    fun plantillaMobileRefs(): Set<String> = extraKnownRefs

    fun emit() = onLinesChange(lines.toList())

    LaunchedEffect(defaultLineAmount, defaultUsdAmount, exchangeQuote) {
        val usdDefault = effectiveUsdLineDefault(defaultUsdAmount, defaultLineAmount, exchangeQuote)
        if (usdDefault.isBlank()) return@LaunchedEffect
        var changed = false
        lines.forEachIndexed { index, line ->
            if (shouldSeedUsdAmount(line, defaultLineAmount, usdDefault)) {
                lines[index] = seedUsdAmountLine(line, usdDefault)
                changed = true
            }
        }
        if (changed) emit()
    }

    fun lineAmountCents(line: PaymentLineDraft): Long =
        if (line.method.usesUsdAmount()) 0L
        else foreignCurrencyLineAmountCents(line, defaultLineAmount, exchangeQuote)

    val planBsCents = parseBulkAmountToCents(totalAmountInput)

    /**
     * Bs registrados (métodos locales). No incluye conversión de $.
     */
    fun assignedCoveringBsCents(skipIndex: Int? = null): Long =
        lines.filterIndexed { i, line -> i != skipIndex && !line.method.usesUsdAmount() }
            .sumOf { line -> foreignCurrencyLineAmountCents(line, defaultLineAmount, exchangeQuote) }

    fun assignedUsdCents(skipIndex: Int? = null): Long =
        lines.filterIndexed { i, line -> i != skipIndex && line.method.usesUsdAmount() }
            .sumOf { line -> foreignCurrencyLineUsdCents(line, defaultLineAmount, exchangeQuote) ?: 0L }

    /** Diferencia que falta por cubrir en Bs, sin contar la línea indicada. */
    fun pendingBsCents(skipIndex: Int? = null): Long =
        (planBsCents - assignedCoveringBsCents(skipIndex)).coerceAtLeast(0L)

    val catalogUsdCents = when (planCatalogUsdAmount) {
        null -> parseBulkAmountToCents(defaultUsdAmount)
        else -> parseBulkAmountToCents(planCatalogUsdAmount)
    }
    val planUsdDisplay = catalogUsdCents.takeIf { it > 0 }
        ?.let { formatMoney(it.toInt(), "USD") }

    LaunchedEffect(defaultLineAmount) {
        emit()
    }

    fun validateLines(): List<PaymentLineDraft>? {
        val target = parseBulkAmountToCents(totalAmountInput)
        if (target <= 0) {
            formError = null
            onChangeGivenCentsChange(0L)
            onChangeGivenUsdCentsChange(0L)
            return emptyList()
        }
        val parsed = lines.map {
            val filledAmount = when {
                it.method.usesUsdAmount() -> it.amountInput
                else -> it.amountInput.ifBlank { defaultLineAmount }
            }
            it.copy(
                amountInput = filledAmount,
                vesAmountInput = if (it.method.usesUsdAmount()) "" else it.vesAmountInput,
                notes = normalizePaymentReference(it.method, it.notes),
                payerPhone = "",
            )
        }
        for (line in parsed) {
            val valid = if (line.method.usesUsdAmount()) {
                (foreignCurrencyLineUsdCents(line, defaultLineAmount, exchangeQuote) ?: 0L) > 0L
            } else {
                lineAmountCents(line) > 0L
            }
            if (!valid) {
                formError = if (line.method.usesUsdAmount() && exchangeRatesEnabled && exchangeQuote == null) {
                    "Cotización BCV no disponible. Configura la tasa en Cotización o intenta actualizar."
                } else if (line.method.usesUsdAmount()) {
                    "Cada línea en dólares debe tener un monto USD válido."
                } else {
                    "Cada línea debe tener un monto válido."
                }
                return null
            }
            paymentReferenceValidationError(line, paymentTypes)?.let {
                formError = it
                return null
            }
        }
        val sum = parsed.sumOf { lineAmountCents(it) }
        val paidUsd = parsed.sumOf {
            if (it.method.usesUsdAmount()) foreignCurrencyLineUsdCents(it, defaultLineAmount, exchangeQuote) ?: 0L
            else 0L
        }
        paymentSumValidationError(
            sumCents = sum,
            targetCents = target,
            allowOverpay = allowOverpay,
            enforcePlanCoverage = enforcePlanCoverage,
            paidUsdCents = paidUsd,
            planUsdCents = catalogUsdCents,
        )?.let {
            formError = it
            return null
        }
        // Vuelto por moneda del excedente: Bs y/o $.
        onChangeGivenCentsChange(parseBulkAmountToCents(changeGivenInput))
        onChangeGivenUsdCentsChange(parseBulkAmountToCents(changeGivenUsdInput))
        formError = null
        return parsed
    }

    Column(modifier) {
        Text(
            "Desglose del cobro",
            fontWeight = FontWeight.SemiBold,
            color = AdminColors.Text,
            fontSize = 13.sp,
        )
        if (paymentDateEditable) {
            Spacer(Modifier.height(6.dp))
            AdminDateField(
                value = paymentDateIso,
                onValueChange = onPaymentDateChange,
                modifier = Modifier.fillMaxWidth(),
                label = "Fecha de pago",
                enabled = enabled,
                outputFormat = AdminDateOutputFormat.ISO,
            )
        }
        Spacer(Modifier.height(4.dp))
        Text(
            "Puedes combinar métodos (ej. efectivo + pago móvil + divisas). " +
                if (enforcePlanCoverage) {
                    "La suma debe cubrir el total a cobrar para confirmar (queda pagada)."
                } else {
                    "Los totales en Bs y \$ se muestran por separado; no se exige que la suma iguale el plan para confirmar."
                },
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        if (planBsCents > 0 || planUsdDisplay != null) {
            Spacer(Modifier.height(8.dp))
            Text(
                "$referenceLabel: " +
                    buildList {
                        if (planBsCents > 0) add(formatMoney(planBsCents.toInt(), GymlyCurrency.CODE))
                        planUsdDisplay?.let { add(it) }
                    }.joinToString(" · "),
                color = AdminColors.Text,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
            )
        }
        Spacer(Modifier.height(10.dp))

        ClientAccountCreditBanner(
            clientId = if (showWalletBanner && walletModuleEnabled) clientId else null,
            pendingAmountCents = parseBulkAmountToCents(totalAmountInput),
            membershipOrderId = membershipOrderId,
            salesOrderId = salesOrderId,
            enabled = enabled,
            deferApply = deferWalletApply,
            highlighted = walletHighlighted,
            initialAppliedCents = walletInitialAppliedCents,
            onCreditApplied = { applied, _ -> onCreditApplied(applied) },
            onBalanceLoaded = onWalletBalanceLoaded,
        )

        if (showWalletBanner && walletModuleEnabled && clientId != null && parseBulkAmountToCents(totalAmountInput) > 0) {
            Spacer(Modifier.height(10.dp))
        }

        val dueCents = parseBulkAmountToCents(totalAmountInput)
        val usdDueCents = parseBulkAmountToCents(defaultUsdAmount)
        // Cobro solo en $ (Bs ya cubiertos / pendiente USD): igual hay que mostrar métodos.
        val showPaymentLines = dueCents > 0 || usdDueCents > 0
        if (!showPaymentLines && showWalletBanner && walletModuleEnabled) {
            Text(
                "El saldo del monedero cubre el monto pendiente. Puedes guardar sin agregar métodos de pago.",
                color = AdminColors.Primary,
                fontSize = 12.sp,
            )
        } else if (!showPaymentLines) {
            // Sin Bs ni $ por cobrar.
        } else {
        if (dueCents <= 0 && usdDueCents > 0) {
            Text(
                "Por cobrar en \$: elige Divisas / Zelle / USDT e indica el monto.",
                color = AdminColors.Primary,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
            )
            Spacer(Modifier.height(8.dp))
        }
        Column(
            Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            lines.forEachIndexed { lineIndex, line ->
                PaymentLineEditor(
                    index = lineIndex,
                    line = line,
                    canRemove = lines.size > 1,
                    enabled = enabled,
                    exchangeQuote = exchangeQuote,
                    exchangeRatesOn = exchangeRatesEnabled,
                    defaultAmount = defaultLineAmount,
                    defaultUsdAmount = defaultUsdAmount,
                    pendingBsCents = pendingBsCents(lineIndex),
                    planUsdDisplay = planUsdDisplay,
                    paymentTypes = paymentTypes,
                    memberId = memberId,
                    knownRefsToday = plantillaMobileRefs(),
                    registeredRefsToday = knownMobileRefsDb,
                    siblingLines = lines,
                    modifier = Modifier.fillMaxWidth(),
                    onUpdate = { updated ->
                        lines[lineIndex] = updated
                        emit()
                    },
                    onRemove = {
                        lines.removeAt(lineIndex)
                        emit()
                    },
                )
            }
        }

        Spacer(Modifier.height(8.dp))
        val pendingBsForNewLine = pendingBsCents()
        val usdCoversPlan = catalogUsdCents > 0L && assignedUsdCents() >= catalogUsdCents
        // Si el cobro en $ ya cubre el precio USD, no insistir en «falta Bs».
        val pendingForNewLine = if (usdCoversPlan) 0L else pendingBsForNewLine
        TextButton(
            onClick = {
                val seed = if (dueCents <= 0 && usdDueCents > 0) {
                    PaymentLineDraft(
                        method = AdminPaymentMethod.DIVISAS,
                        amountInput = defaultUsdAmount,
                    )
                } else if (usdCoversPlan && pendingBsForNewLine <= 0) {
                    PaymentLineDraft(
                        method = AdminPaymentMethod.DIVISAS,
                        amountInput = defaultUsdAmount.ifBlank { "0" },
                    )
                } else {
                    PaymentLineDraft(amountInput = centsToAmountInput(pendingBsForNewLine.coerceAtLeast(0L)))
                }
                lines.add(seed)
                emit()
            },
            enabled = enabled && showPaymentLines,
        ) {
            Icon(Icons.Outlined.Add, contentDescription = null, tint = AdminColors.Primary)
            Spacer(Modifier.width(4.dp))
            Text(
                when {
                    pendingForNewLine > 0 ->
                        "Agregar método (falta ${formatMoney(pendingForNewLine.toInt(), GymlyCurrency.CODE)})"
                    usdCoversPlan ->
                        "Agregar método (cubierto en \$)"
                    dueCents <= 0 && usdDueCents > 0 ->
                        "Agregar método (\$)"
                    else ->
                        "Agregar método"
                },
                color = AdminColors.Primary,
            )
        }
        }

        val target = dueCents
        val currentBs = paymentLinesBsCents(lines, defaultLineAmount, exchangeQuote)
        val currentUsd = paymentLinesUsdCents(lines, defaultLineAmount, exchangeQuote)
        if (target > 0 || currentUsd > 0 || currentBs > 0) {
            Spacer(Modifier.height(6.dp))
            val matches = if (!enforcePlanCoverage) {
                true
            } else {
                paymentSumValidationError(
                    sumCents = currentBs,
                    targetCents = target,
                    allowOverpay = allowOverpay,
                    enforcePlanCoverage = true,
                    paidUsdCents = currentUsd,
                    planUsdCents = catalogUsdCents,
                ) == null
            }
            val methodBreakdown = paymentLinesByMethodLabels(lines, defaultLineAmount, exchangeQuote)
            if (methodBreakdown.isNotEmpty()) {
                Text(
                    methodBreakdown.joinToString(" · "),
                    color = AdminColors.Text,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                )
                Spacer(Modifier.height(2.dp))
            }
            Text(
                "Total Bs: ${formatMoney(currentBs.toInt(), GymlyCurrency.CODE)}" +
                    if (currentUsd > 0) " · Total \$: ${formatMoney(currentUsd.toInt(), "USD")}" else "",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                fontWeight = FontWeight.Medium,
            )
            Spacer(Modifier.height(2.dp))
            Text(
                "Referencia plan: ${formatMoney(target.toInt(), GymlyCurrency.CODE)}" +
                    (planUsdDisplay?.let { " · $it" } ?: "") +
                    if (enforcePlanCoverage) {
                        " · aplicado ${formatMoney(currentBs.toInt(), GymlyCurrency.CODE)}" +
                            if (currentUsd > 0) " · ${formatMoney(currentUsd.toInt(), "USD")}" else ""
                    } else {
                        " (informativo; no bloquea el cobro)"
                    },
                color = if (matches) AdminColors.Primary else AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            if (enforcePlanCoverage && walletModuleEnabled) {
                walletOverpayHint(currentBs, target, multiClientOverpay)?.let { hint ->
                    Spacer(Modifier.height(4.dp))
                    Text(hint, color = AdminColors.Primary, fontSize = 12.sp)
                }
            }
            // Vuelto casi siempre en Bs (también si el excedente vino de Divisas/Zelle/USDT).
            val coveringBs = currentBs
            val planUsdForOverpay = catalogUsdCents.coerceAtLeast(0L)
            val usdOverpayCents = if (planUsdForOverpay > 0) {
                (currentUsd - planUsdForOverpay).coerceAtLeast(0L)
            } else if (currentUsd > 0 && coveringBs >= target) {
                currentUsd
            } else {
                0L
            }
            val bsOverpay = (coveringBs - target).coerceAtLeast(0L)
            val hasOverpay = bsOverpay > 0 || usdOverpayCents > 0
            val changeGivenBsCents = parseBulkAmountToCents(changeGivenInput)
            // Monedero solo con excedente Bs no entregado como vuelto.
            val walletCredit = if (walletModuleEnabled && bsOverpay > 0) {
                (bsOverpay - changeGivenBsCents).coerceAtLeast(0L)
            } else {
                0L
            }
            if (allowOverpay && walletModuleEnabled && walletCredit > 0) {
                Spacer(Modifier.height(6.dp))
                Text(
                    if (changeGivenBsCents > 0) {
                        "Tras el vuelto, ${formatMoney(walletCredit.toInt(), GymlyCurrency.CODE)} " +
                            "se acreditará al monedero digital del cliente."
                    } else {
                        "${formatMoney(walletCredit.toInt(), GymlyCurrency.CODE)} se acreditará al " +
                            "monedero digital del cliente (no indicaste vuelto)."
                    },
                    color = AdminColors.Primary,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                )
            }
            if (allowOverpay && hasOverpay) {
                Spacer(Modifier.height(10.dp))
                Text(
                    "Vuelto entregado (Bs)",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
                Spacer(Modifier.height(4.dp))
                OutlinedTextField(
                    value = changeGivenInput,
                    onValueChange = {
                        changeGivenInput = it
                        onChangeGivenCentsChange(parseBulkAmountToCents(it))
                    },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = enabled,
                    singleLine = true,
                    placeholder = {
                        Text("Monto del vuelto en bolívares", color = AdminColors.TextMuted)
                    },
                    prefix = { Text("Bs ", color = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                Text(
                    when {
                        bsOverpay > 0 && usdOverpayCents > 0 ->
                            "El vuelto se entrega en Bs (excedente Bs ${formatMoney(bsOverpay.toInt(), GymlyCurrency.CODE)}; " +
                                "excedente \$ ${formatMoney(usdOverpayCents.toInt(), "USD")})."
                        usdOverpayCents > 0 ->
                            "El vuelto se entrega en Bs aunque el excedente sea en \$ " +
                                "(${formatMoney(usdOverpayCents.toInt(), "USD")})."
                        else ->
                            "El vuelto se entrega en Bs (excedente ${formatMoney(bsOverpay.toInt(), GymlyCurrency.CODE)})."
                    },
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }
        }

        formError?.let {
            Spacer(Modifier.height(8.dp))
            Text(it, color = AdminColors.Error, fontSize = 12.sp)
        }

        content(lines, formError, parseBulkAmountToCents(changeGivenInput)) { validateLines() }
    }
}

@Composable
private fun PaymentLineEditor(
    index: Int,
    line: PaymentLineDraft,
    canRemove: Boolean,
    enabled: Boolean,
    exchangeQuote: AdminExchangeRateQuote?,
    exchangeRatesOn: Boolean = true,
    defaultAmount: String,
    defaultUsdAmount: String,
    pendingBsCents: Long,
    planUsdDisplay: String?,
    paymentTypes: List<org.gymly.model.AdminPaymentMethodType>?,
    knownRefsToday: Set<String>,
    registeredRefsToday: Map<String, String>,
    siblingLines: List<PaymentLineDraft>,
    memberId: String? = null,
    modifier: Modifier = Modifier,
    onUpdate: (PaymentLineDraft) -> Unit,
    onRemove: () -> Unit,
) {
    Column(
        modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .background(AdminColors.Surface)
            .padding(14.dp),
    ) {
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text("Línea ${index + 1}", fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
            if (canRemove) {
                IconButton(onClick = onRemove, enabled = enabled) {
                    Icon(Icons.Outlined.Close, "Quitar línea", tint = AdminColors.Error)
                }
            }
        }
        Spacer(Modifier.height(10.dp))
        AdminPaymentMethodIconGrid(
            selected = line.method,
            onSelect = { selected ->
                val updated = line.copy(method = selected, dualCurrencyLinked = true)
                if (selected.usesUsdAmount()) {
                    val usdDefault = effectiveUsdLineDefault(defaultUsdAmount, defaultAmount, exchangeQuote)
                    onUpdate(
                        seedUsdAmountLine(
                            updated.copy(amountInput = "", vesAmountInput = ""),
                            usdDefault,
                        ),
                    )
                } else {
                    val pendingInput = centsToAmountInput(pendingBsCents).ifBlank { defaultAmount }
                    val bsAmount = if (line.method.usesUsdAmount()) {
                        pendingInput
                    } else {
                        line.amountInput.ifBlank { pendingInput }
                    }
                    onUpdate(
                        updated.copy(
                            amountInput = bsAmount,
                            vesAmountInput = "",
                        ),
                    )
                }
            },
            enabled = enabled,
            columnsPerRow = 4,
            paymentTypes = paymentTypes,
        )
        line.method.methodHint()?.let {
            Spacer(Modifier.height(6.dp))
            Text(it, color = AdminColors.TextMuted, fontSize = 12.sp)
        }
        Spacer(Modifier.height(12.dp))
        PaymentLineAmountFields(
            line = line,
            enabled = enabled,
            exchangeQuote = exchangeQuote,
            exchangeRatesOn = exchangeRatesOn,
            defaultAmount = defaultAmount,
            defaultUsdAmount = defaultUsdAmount,
            pendingBsCents = pendingBsCents,
            planUsdDisplay = planUsdDisplay,
            paymentTypes = paymentTypes,
            knownRefsToday = knownRefsToday,
            registeredRefsToday = registeredRefsToday,
            siblingLines = siblingLines,
            memberId = memberId,
            onUpdate = onUpdate,
            modifier = Modifier.fillMaxWidth(),
        )
    }
}

@Composable
private fun PaymentLineAmountFields(
    line: PaymentLineDraft,
    enabled: Boolean,
    exchangeQuote: AdminExchangeRateQuote?,
    exchangeRatesOn: Boolean = true,
    defaultAmount: String,
    defaultUsdAmount: String,
    pendingBsCents: Long,
    planUsdDisplay: String?,
    paymentTypes: List<org.gymly.model.AdminPaymentMethodType>?,
    knownRefsToday: Set<String>,
    registeredRefsToday: Map<String, String>,
    siblingLines: List<PaymentLineDraft>,
    memberId: String? = null,
    onUpdate: (PaymentLineDraft) -> Unit,
    modifier: Modifier = Modifier,
) {
    val needReference = line.method.requiresReferenceConfigured(paymentTypes)
    val needProof = line.method.requiresProofConfigured(paymentTypes)
    Column(modifier) {
        if (line.method.showsPlanUsdReference() && !planUsdDisplay.isNullOrBlank()) {
            Text(
                "Precio del plan: $planUsdDisplay",
                color = AdminColors.Text,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
            Spacer(Modifier.height(8.dp))
        }
        if (line.method.usesUsdAmount()) {
            DualCurrencyAmountFields(
                usdInput = line.amountInput,
                vesInput = line.vesAmountInput,
                onUsdChange = { raw -> onUpdate(applyDualCurrencyUsdChange(line, raw, exchangeQuote)) },
                onVesChange = { raw -> onUpdate(applyDualCurrencyVesChange(line, raw, exchangeQuote)) },
                exchangeQuote = exchangeQuote,
                exchangeRatesOn = exchangeRatesOn,
                enabled = enabled,
                showVesField = false,
            )
            Spacer(Modifier.height(4.dp))
            Text(
                "Solo se registra el monto en USD. No se suma a Total Bs (los Bs van en efectivo, móvil, etc.).",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
        } else {
            Text(line.method.amountInputLabel(), color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = line.amountInput,
                onValueChange = { onUpdate(line.copy(amountInput = it)) },
                enabled = enabled,
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            // Alerta: un monto en Bs mayor a lo que falta por cobrar. Puede ser un error de tipeo o un pago de más
            // (en ese caso debe declararse el vuelto). No bloquea: la cajera revisa antes de cobrar.
            if (pendingBsCents > 0 && !line.method.usesUsdAmount() &&
                parseBulkAmountToCents(line.amountInput) > pendingBsCents
            ) {
                Spacer(Modifier.height(4.dp))
                Text(
                    "Supera lo que falta por cobrar (${formatMoney(pendingBsCents.toInt(), GymlyCurrency.CODE)}). " +
                        "Revisa el monto; si la persona pagó de más, declara el vuelto.",
                    color = AdminColors.Error,
                    fontSize = 12.sp,
                )
            }
            if (pendingBsCents > 0 && parseBulkAmountToCents(line.amountInput) != pendingBsCents) {
                Spacer(Modifier.height(4.dp))
                TextButton(
                    onClick = { onUpdate(line.copy(amountInput = centsToAmountInput(pendingBsCents))) },
                    enabled = enabled,
                ) {
                    Text(
                        "Usar la diferencia (${formatMoney(pendingBsCents.toInt(), GymlyCurrency.CODE)})",
                        color = AdminColors.Primary,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                    )
                }
            }
        }
        if (needReference) {
            Spacer(Modifier.height(10.dp))
            Text(
                if (line.method == AdminPaymentMethod.MOBILE_PAYMENT) {
                    "Referencia (últimos 5 dígitos)"
                } else {
                    "Referencia"
                },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = line.notes,
                onValueChange = { onUpdate(line.copy(notes = paymentLineReferenceInput(line.method, it))) },
                enabled = enabled,
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = {
                    Text(
                        if (line.method == AdminPaymentMethod.MOBILE_PAYMENT) "12345" else "Nº de referencia / operación",
                        color = AdminColors.TextMuted,
                    )
                },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )
            paymentReferenceDuplicateWarning(line, siblingLines, knownRefsToday, registeredRefsToday)?.let { alert ->
                Spacer(Modifier.height(4.dp))
                Text(alert, color = Color(0xFFE65100), fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
            }
            if (line.method == AdminPaymentMethod.MOBILE_PAYMENT) {
                Spacer(Modifier.height(4.dp))
                Text(
                    "Si varias personas pagan con el mismo pago móvil, usa la misma referencia y el mismo comprobante.",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
        }
        if (needProof) {
            if (needReference) {
                Spacer(Modifier.height(6.dp))
            } else {
                Spacer(Modifier.height(10.dp))
            }
            Row(verticalAlignment = Alignment.CenterVertically) {
                FileUploadIconButton(
                    onClick = {
                        openProofFilePicker { picked ->
                            if (picked == null) return@openProofFilePicker
                            onUpdate(
                                line.copy(
                                    proof = PickedImagePayload(picked.bytes, picked.fileName, picked.mimeType),
                                    proofName = picked.fileName,
                                ),
                            )
                        }
                    },
                    tooltip = if (line.proofName != null) "Cambiar comprobante" else "Subir comprobante",
                    enabled = enabled,
                    tint = AdminColors.Primary,
                    icon = Icons.Outlined.Image,
                )
                // TODO(IA): escáner de comprobantes suspendido (llama a /api/payments/scan, no compilado en el backend).
                // PaymentScannerButton(
                //     memberId = memberId,
                //     enabled = enabled,
                //     onResult = { state ->
                //         when (state) {
                //             is PaymentUiState.Success -> {
                //                 val data = state.data.extractedData
                //                 if (data != null) {
                //                     onUpdate(
                //                         line.copy(
                //                             amountInput = data.monto.toString(),
                //                             notes = "${data.referencia} ${data.banco ?: ""}".trim(),
                //                         ),
                //                     )
                //                 }
                //             }
                //             is PaymentUiState.PendingApproval -> {}
                //             is PaymentUiState.Error -> {}
                //             else -> {}
                //         }
                //     }
                // )
                Text(
                    line.proofName ?: "Comprobante (opcional)",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(start = 8.dp),
                )
            }
        }
    }
}

fun resolveCombinedPaymentStatus(lines: List<PaymentLineDraft>, defaultAmount: String): org.gymly.model.AdminMembershipOrderStatus {
    if (lines.isEmpty()) return org.gymly.model.AdminMembershipOrderStatus.PAID
    val keepsPending = lines.any { line ->
        line.method.requiresManualApproval() &&
            line.method != org.gymly.model.AdminPaymentMethod.TRANSFER &&
            line.method != org.gymly.model.AdminPaymentMethod.MOBILE_PAYMENT
    }
    return if (keepsPending) {
        org.gymly.model.AdminMembershipOrderStatus.PENDING
    } else {
        org.gymly.model.AdminMembershipOrderStatus.PAID
    }
}

fun paymentLinesSummaryLabel(lines: List<PaymentLineDraft>, defaultAmount: String): String {
    if (lines.isEmpty()) return "—"
    if (lines.size == 1) return lines.first().method.labelEs()
    return lines.joinToString(" + ") { line ->
        val amountLabel = if (line.method.usesDualCurrency()) {
            val usd = line.amountInput.ifBlank { "0" }
            "$${usd}"
        } else {
            formatMoney(line.amountCents(defaultAmount).toInt(), GymlyCurrency.CODE)
        }
        "${line.method.labelEs()} $amountLabel"
    }
}
